<template>
	<!-- CZ 12절: 직원 본인 KPI 카드. 대시보드(compact)와 주간 KPI 화면(내 KPI)이 같은 카드를 쓴다. -->
	<div class="section kpi-card" v-if="loaded && me">
		<div class="section-tit">
			이번 주 KPI
			<span class="sub">{{ weekLabel }}</span>
			<div class="right">
				<button type="button" class="btn btn-sm btn-light" @click="openGoal">목표 정하기</button>
				<router-link v-if="compact" class="btn btn-sm btn-secondary" to="/WorkBoard">자세히</router-link>
			</div>
		</div>

		<div class="kpi-card__body">
			<div class="kpi-card__main">
				<div class="kpi-card__label">KPI</div>
				<div class="kpi-card__figure">
					<b>{{ me.call_total }}</b><span class="kpi-card__of"> / {{ me.target_call }}</span>
					<span class="kpi-card__pct" v-bind:class="rateClass">{{ pct(me.call_rate) }}</span>
				</div>
				<div class="kpi-card__bar"><span v-bind:class="rateClass" :style="{ width: barWidth }"></span></div>
				<div class="kpi-card__short">
					<template v-if="me.call_short > 0 || me.new_short > 0">
						목표까지
						<b v-if="me.call_short > 0" class="txt-c--red">전화 {{ me.call_short }}통</b>
						<template v-if="me.call_short > 0 && me.new_short > 0">, </template>
						<b v-if="me.new_short > 0" class="txt-c--red">신규매물 {{ me.new_short }}건</b>
						부족
					</template>
					<b v-else class="kpi-ok">이번 주 목표 달성</b>
				</div>
			</div>

			<table class="table type-input kpi-card__table">
				<colgroup><col width="120" /><col /></colgroup>
				<tr>
					<th>실제 전화</th>
					<td>
						<b>{{ me.calls }}</b>통
						<span class="txt-c--grey" v-if="me.missed > 0">(부재 {{ me.missed }}건은 KPI 제외)</span>
					</td>
				</tr>
				<tr>
					<th>미팅·현장 인정</th>
					<td>
						<b>+{{ me.credit_applied }}</b>
						<span class="txt-c--grey">
							매도미팅 {{ me.meet_sell }} · 매수미팅 {{ me.meet_buy }} · 현장안내 {{ me.site }}
							<template v-if="me.credit_raw > me.credit_applied"> (인정 {{ me.credit_raw }} 중 상한 {{ me.credit_applied }})</template>
						</span>
					</td>
				</tr>
				<tr>
					<th>신규매물</th>
					<td><b>{{ me.new_cnt }}</b> / {{ me.target_new }}건</td>
				</tr>
				<tr v-if="!compact">
					<th>작업내역</th>
					<td>{{ me.raw_logs }}건 <span class="txt-c--grey">(같은 매물에 이어 적은 기록은 1통으로 셉니다)</span></td>
				</tr>
				<tr>
					<th>기한초과 작업</th>
					<td><b v-bind:class="{ 'txt-c--red': me.over_cnt > 0 }">{{ me.over_cnt }}</b>건</td>
				</tr>
				<tr>
					<th>신규 매수배정</th>
					<td>
						<template v-if="assign">
							<b v-bind:class="assignClass">{{ assignLabel }}</b>
							<div v-for="(r, i) in assign.reasons" :key="'ar_' + i" class="kpi-card__reason">사유: {{ r.text }}</div>
							<div v-if="assign.status === 'OFF'" class="kpi-card__reason">배정 대상이 아닙니다(관리자 설정).</div>
							<div v-else-if="!assign.kpiActive && assign.kpiStartWeek" class="kpi-card__reason txt-c--grey">
								KPI 판정은 {{ assign.kpiStartWeek }} 주 마감부터 적용됩니다.
							</div>
						</template>
						<span v-else class="txt-c--grey">-</span>
					</td>
				</tr>
				<tr v-if="lastWeek && lastWeek.row && lastWeek.row.pass_flag !== 'X'">
					<th>지난주</th>
					<td>
						KPI {{ lastWeek.row.call_total }} / {{ lastWeek.row.target_call }}
						({{ pct(lastWeek.row.call_rate) }}) · 신규매물 {{ lastWeek.row.new_cnt }} / {{ lastWeek.row.target_new }}건
						<b v-if="lastWeek.row.pass_flag === 'Y'" class="kpi-ok">기준 충족</b>
						<!-- 평가 시작 주 이전 결과는 배정에 쓰지 않는다. 빨간 '미달' 로 두면 막힌 것처럼 읽힌다. -->
						<span v-else-if="assign && !assign.kpiActive" class="txt-c--grey">기준 미달(평가 시작 전이라 배정에 반영되지 않음)</span>
						<b v-else class="txt-c--red">기준 미달</b>
					</td>
				</tr>
			</table>
		</div>

		<div v-if="leads.length" class="kpi-card__leads">
			<b>첫 연락을 기다리는 신규 매수문의 {{ leads.length }}건</b>
			<span class="txt-c--grey">고객카드에 상담을 남기면 사라집니다.</span>
			<div class="kpi-card__lead-list">
				<button
					v-for="l in leads"
					:key="'ld_' + l.lead_uid"
					type="button"
					class="btn btn-xsm"
					v-bind:class="l.overdue ? 'btn-danger' : 'btn-outline-primary'"
					@click="openClient(l.client_uid)"
				>
					{{ l.name }} <span class="kpi-card__lead-at">{{ l.assigned_at.slice(5) }} 배정</span>
				</button>
			</div>
		</div>

		<!-- 개인 목표: 회사 최소기준 이상만(3절). 비우면 최소기준을 그대로 쓴다. -->
		<div v-if="goal" class="popup-wrap">
			<div class="dim" @click="goal = null"></div>
			<div class="popup sm">
				<div class="popup-tit">
					주간 목표
					<button type="button" class="btn btn-close" @click="goal = null"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<div class="segment kpi-goal__weeks">
						<button
							type="button"
							class="btn btn-sm"
							v-bind:class="goal.week === thisWeek ? 'btn-primary' : 'btn-light'"
							@click="loadGoal(thisWeek)"
						>
							이번 주
						</button>
						<button
							type="button"
							class="btn btn-sm"
							v-bind:class="goal.week === nextWeek ? 'btn-primary' : 'btn-light'"
							@click="loadGoal(nextWeek)"
						>
							다음 주
						</button>
					</div>
					<table class="table type-input">
						<colgroup><col width="40%" /><col width="60%" /></colgroup>
						<tr>
							<th>주간</th>
							<td>{{ goal.week }} 시작</td>
						</tr>
						<tr>
							<th>전화 목표</th>
							<td>
								<input type="number" class="form-control" :min="goal.min_call" :placeholder="'최소 ' + goal.min_call" v-model="goal.goal_call" />
							</td>
						</tr>
						<tr>
							<th>신규매물 목표</th>
							<td>
								<input type="number" class="form-control" :min="goal.min_new" :placeholder="'최소 ' + goal.min_new" v-model="goal.goal_new_item" />
							</td>
						</tr>
					</table>
					<div class="txt-c--grey">
						※ 회사 최소기준(전화 {{ goal.min_call }}통, 신규매물 {{ goal.min_new }}건)보다 낮게 정할 수 없습니다. 비워 두면
						최소기준이 목표가 됩니다. 신규 매수배정은 개인 목표가 아니라 회사 최소기준으로 판정합니다.
					</div>
					<div class="btn-wrap">
						<button type="button" class="btn btn-sm btn-secondary" @click="goal = null">취소</button>
						<button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="saveGoal">{{ saving ? '저장 중' : '저장' }}</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
function addDays(ymd, n) {
	const d = new Date(ymd + 'T00:00:00Z');
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
}
const DOW = ['일', '월', '화', '수', '목', '금', '토'];

export default {
	name: 'KpiCard',
	props: {
		compact: { type: Boolean, default: false },
	},
	emits: ['loaded'],
	data() {
		return {
			loaded: false,
			me: null,
			week: null,
			assign: null,
			lastWeek: null,
			leads: [],
			goal: null,
			saving: false,
		};
	},
	computed: {
		thisWeek() {
			return this.week ? this.week.start : '';
		},
		nextWeek() {
			return this.week ? addDays(this.week.start, 7) : '';
		},
		weekLabel() {
			if (!this.week) return '';
			const f = (s) => `${Number(s.slice(5, 7))}.${Number(s.slice(8, 10))}(${DOW[new Date(s + 'T00:00:00Z').getUTCDay()]})`;
			return `${f(this.week.start)} ~ ${f(this.week.end)}`;
		},
		rateClass() {
			const r = Number(this.me && this.me.call_rate) || 0;
			if (r >= 100) return 'is-ok';
			if (r >= 70) return 'is-warn';
			return 'is-low';
		},
		barWidth() {
			const r = Math.max(0, Math.min(100, Number(this.me && this.me.call_rate) || 0));
			return r + '%';
		},
		assignLabel() {
			if (!this.assign) return '';
			return { PASS: 'PASS', HOLD: 'HOLD', OFF: '배정 대상 아님' }[this.assign.status] || this.assign.status;
		},
		assignClass() {
			if (!this.assign) return '';
			return { PASS: 'kpi-ok', HOLD: 'txt-c--red', OFF: 'txt-c--grey' }[this.assign.status] || '';
		},
	},
	created() {
		this.load();
	},
	methods: {
		pct(v) {
			return v == null ? '-' : `${v}%`;
		},
		async load() {
			const re = await this.$apiGET('/admin/api/kpi/me');
			if (!re || !re.week) return;
			this.me = re.me;
			this.week = re.week;
			this.assign = re.assign;
			this.lastWeek = re.last_week;
			this.leads = re.leads || [];
			this.loaded = true;
			this.$emit('loaded', re);
		},
		async loadGoal(week) {
			const re = await this.$apiGET('/admin/api/kpi/goal?week=' + week);
			if (!re || !re.week) return;
			this.goal = {
				week: re.week,
				min_call: re.min_call,
				min_new: re.min_new,
				goal_call: re.goal_call == null ? '' : re.goal_call,
				goal_new_item: re.goal_new_item == null ? '' : re.goal_new_item,
			};
		},
		openGoal() {
			this.loadGoal(this.thisWeek);
		},
		async saveGoal() {
			if (this.saving) return;
			this.saving = true;
			try {
				const re = await this.$apiPOST('/admin/api/kpi/goal', {
					week: this.goal.week,
					goal_call: this.goal.goal_call,
					goal_new_item: this.goal.goal_new_item,
				});
				if (!re || !re.ok) return;
				this.goal = null;
				this.load();
			} finally {
				this.saving = false;
			}
		},
		openClient(cid) {
			if (!cid) return;
			const w = window.screen.availWidth;
			const h = window.screen.availHeight;
			window.open('/UserDetail/' + cid, '', `width=${w}, height=${h}, resizable=no, status=no`);
		},
	},
};
</script>

<style lang="scss">
@import "@/assets/css/design-tokens.scss";

.kpi-card {
	&__body {
		display: flex;
		gap: $dt-spacing-gap;
		align-items: flex-start;
	}
	&__main {
		flex: 0 0 260px;
		padding: $dt-spacing-cell-head 0;
	}
	&__label {
		@include dt-type-dense;
		color: $dt-color-ink-faint;
	}
	&__figure {
		white-space: nowrap;
		b {
			font-size: 40px;
			font-weight: 700;
			line-height: 1.1;
			color: $dt-color-ink;
		}
	}
	&__of {
		font-size: 20px;
		color: $dt-color-ink-muted;
	}
	&__pct {
		margin-left: 10px;
		font-size: 20px;
		font-weight: 600;
	}
	.is-ok {
		color: $dt-color-success-ink;
	}
	.is-warn {
		color: $dt-color-warning-ink;
	}
	.is-low {
		color: $dt-color-danger;
	}
	&__bar {
		height: 6px;
		margin: 8px 0 10px;
		background: $dt-color-line;
		border-radius: $dt-rounded-chip;
		overflow: hidden;
		span {
			display: block;
			height: 100%;
			&.is-ok {
				background: $dt-color-success;
			}
			&.is-warn {
				background: $dt-color-warning;
			}
			&.is-low {
				background: $dt-color-danger;
			}
		}
	}
	&__short {
		@include dt-type-dense;
	}
	&__table {
		flex: 1 1 auto;
		margin: 0;
		td {
			text-align: left;
		}
	}
	&__reason {
		@include dt-type-dense;
		color: $dt-color-danger;
	}
	&__leads {
		margin-top: $dt-spacing-cell-x;
		padding-top: $dt-spacing-cell-x;
		border-top: 1px solid $dt-color-line-strong;
		@include dt-type-dense;
		b {
			margin-right: 8px;
		}
	}
	&__lead-list {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 6px;
	}
	&__lead-at {
		opacity: 0.8;
		margin-left: 4px;
	}
	.kpi-ok {
		color: $dt-color-success-ink;
	}
	.kpi-goal__weeks {
		display: flex;
		gap: 4px;
		margin-bottom: $dt-spacing-cell-x;
	}
}
</style>
